import { createSelector } from 'reselect';
import { DefinedEventRecord } from '@fullstory/proto/fs/services/search-settings';
import { AppState } from '../../../types';
import { definedEventCanBeCooked } from '@fullstory/search-types';
import { getIsReady } from '@fullstory/redux-util';

export const getDefinedEventsById = (state: AppState): Map<string, DefinedEventRecord> =>
  state.searchMeta.definedEvents ?? new Map();
export const getDefinedEventsAccess = (state: AppState) => state.searchMeta.definedEventsAccess;
export const getAllDefinedEventsLoading = (state: AppState) =>
  state.searchMeta.getAllDefinedEvents.loading;
export const getAllDefinedEventsReady = (state: AppState) =>
  getIsReady(state.searchMeta.getAllDefinedEvents);

// Includes archived and fs_managed.
export const getAllDefinedEvents = createSelector(getDefinedEventsById, byId => {
  const definedEvents = [...byId.values()];
  definedEvents.sort((a, b) => {
    if (!a.definedEvent || !b.definedEvent) return 0;
    if (a.definedEvent.name > b.definedEvent.name) return 1;
    else if (b.definedEvent.name > a.definedEvent.name) return -1;

    return 0;
  });
  return definedEvents;
});

export const getUnarchivedDefinedEventsByName = createSelector(getDefinedEventsById, byId => {
  const byName = new Map<string, DefinedEventRecord>();

  if (!byId) {
    return byName;
  }

  byId.forEach((de: DefinedEventRecord) => {
    if (!de?.metadata?.archived && de?.definedEvent) {
      byName.set(de.definedEvent.name, de);
    }
  });

  return byName;
});

export const getUnarchivedDefinedEventsById = createSelector(getDefinedEventsById, byId => {
  const unarchivedById = new Map<string, DefinedEventRecord>();

  if (!byId) {
    return unarchivedById;
  }

  byId.forEach(de => {
    if (!de?.metadata?.archived && de.definedEvent) {
      unarchivedById.set(de.definedEvent.id, de);
    }
  });

  return unarchivedById;
});

export const getUnarchivedDefinedEvents = createSelector(getAllDefinedEvents, definedEvents =>
  definedEvents.filter(de => !de?.metadata?.archived),
);

export const getCookableDefinedEvents = createSelector(getUnarchivedDefinedEvents, definedEvents =>
  definedEvents.filter(de => de.definedEvent && definedEventCanBeCooked(de.definedEvent)),
);

export const getActiveEventCount = createSelector(
  getUnarchivedDefinedEvents,
  events => events.length,
);

export const getActiveEventLimit = (state: AppState): number =>
  state.searchMeta.getAllDefinedEvents.data?.activeLimit ?? -1;
